import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { DeleteButton, ErrorBanner, Field, Loading, Modal } from '../../components/ui';
import { api } from '../../lib/api';
import { useResource } from '../../lib/hooks';
import {
  GWC_ACTIONS,
  GWC_ANSWERS,
  GWC_DIMENSIONS,
  VALUE_RATINGS,
  VERDICTS,
  verdictFor,
  type CoreValue,
  type GwcPerson,
  type Rating,
} from './maGwcMeta';

/**
 * GWC — the EOS People Analyzer for the staff coming across.
 *
 * Separate from this app's own GWC Assessment, which is keyed to core.Employee:
 * these people are not employees here and may never be. See migration 030.
 */
interface Payload {
  people: GwcPerson[];
  values: CoreValue[];
  ratings: Rating[];
}

export function MaGwc() {
  const data = useResource<Payload>('/ma/gwc');
  const [open, setOpen] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const people = useMemo(() => data.data?.people ?? [], [data.data]);
  const values = useMemo(() => data.data?.values ?? [], [data.data]);
  const ratings = useMemo(() => data.data?.ratings ?? [], [data.data]);

  const rate = async (personId: string, valueId: number, rating: string) => {
    setProblem(null);
    try {
      await api.put(`/ma/gwc/people/${personId}/ratings/${valueId}`, { rating });
      data.reload();
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Could not save');
    }
  };

  if (data.loading) return <Loading label="Loading the assessment" />;
  if (data.error) return <ErrorBanner message={data.error} />;

  const person = people.find((p) => p.MaGwcPersonId === open) ?? null;
  const rightSeat = people.filter(
    (p) => p.GetIt === 'yes' && p.WantIt === 'yes' && p.Capacity === 'yes',
  ).length;

  return (
    <div className="stack">
      {problem && <ErrorBanner message={problem} />}

      <div className="card">
        <div className="card-body tight">
          <div className="row wrap">
            <span className="eyebrow">Core values</span>
            {/* The company's own values, from the V/TO (migration 180). They are
                decided and edited there, so this grid rates against them rather
                than keeping a second list that drifts. */}
            {values.length === 0 ? (
              <span className="xs muted">
                None yet. Add them on the V/TO; until then, GWC answers the right-seat half only.
              </span>
            ) : (
              values.map((v) => (
                <span key={v.CoreValueId} className="badge badge-neutral" title={v.Description ?? undefined}>
                  {v.Name}
                </span>
              ))
            )}
            <Link to="/vto" className="btn btn-ghost btn-sm">
              Edit on the V/TO
            </Link>
            <span className="grow" />
            <span className="xs muted">
              <span className="mono">{rightSeat}</span> of <span className="mono">{people.length}</span> right
              seat
            </span>
            <button type="button" className="btn btn-primary btn-sm" onClick={() => setAdding(true)}>
              Add a person
            </button>
          </div>
        </div>
      </div>

      {people.length === 0 ? (
        <div className="card">
          <div className="card-body">
            <p className="muted">
              Nobody added yet. Add the staff coming across with the acquisition, then answer the
              two EOS questions for each: are they the right person, and are they in the right seat.
            </p>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Seat</th>
                  {GWC_DIMENSIONS.map((d) => (
                    <th key={d.key} className="num" title={d.label}>
                      {d.letter}
                    </th>
                  ))}
                  {values.map((v) => (
                    <th key={v.CoreValueId} className="num ma-gwc-value" title={v.Name}>
                      {v.Name}
                    </th>
                  ))}
                  <th>Verdict</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {people.map((p) => {
                  const key = verdictFor(p, values, ratings);
                  const verdict = VERDICTS[key];
                  return (
                    <tr key={p.MaGwcPersonId}>
                      <td>
                        <div className="strong">{p.Name}</div>
                        {p.Department && <div className="xs muted">{p.Department}</div>}
                      </td>
                      <td className="xs">{p.Seat || <span className="subtle">—</span>}</td>

                      {GWC_DIMENSIONS.map((d) => {
                        const v = p[d.key];
                        return (
                          <td key={d.key} className="num">
                            <span
                              className={`ma-gwc-mark${v === 'yes' ? ' yes' : v === 'no' ? ' no' : ''}`}
                              title={GWC_ANSWERS.find((a) => a.value === v)?.label}
                            >
                              {GWC_ANSWERS.find((a) => a.value === v)?.short ?? '?'}
                            </span>
                          </td>
                        );
                      })}

                      {values.map((val) => {
                        const r =
                          ratings.find(
                            (x) =>
                              x.MaGwcPersonId === p.MaGwcPersonId &&
                              x.CoreValueId === val.CoreValueId,
                          )?.Rating ?? 'unrated';
                        return (
                          <td key={val.CoreValueId} className="num">
                            <select
                              className="select ma-gwc-rate"
                              value={r}
                              onChange={(e) =>
                                void rate(p.MaGwcPersonId, val.CoreValueId, e.target.value)
                              }
                            >
                              {VALUE_RATINGS.map((x) => (
                                <option key={x.value} value={x.value}>
                                  {x.short}
                                </option>
                              ))}
                            </select>
                          </td>
                        );
                      })}

                      <td>
                        <span className={`badge ${verdict.badge}`}>{verdict.label}</span>
                      </td>
                      <td className="actions">
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => setOpen(p.MaGwcPersonId)}
                        >
                          Assess
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {person && (
        <PersonEditor
          person={person}
          values={values}
          ratings={ratings}
          onClose={() => setOpen(null)}
          onSaved={() => data.reload()}
          onDeleted={() => {
            setOpen(null);
            data.reload();
          }}
        />
      )}

      {adding && (
        <AddPerson
          onClose={() => setAdding(false)}
          onSaved={() => {
            setAdding(false);
            data.reload();
          }}
        />
      )}

    </div>
  );
}

function PersonEditor({
  person,
  values,
  ratings,
  onClose,
  onSaved,
  onDeleted,
}: {
  person: GwcPerson;
  values: CoreValue[];
  ratings: Rating[];
  onClose: () => void;
  onSaved: () => void;
  onDeleted: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const key = verdictFor(person, values, ratings);
  const verdict = VERDICTS[key];

  const save = async (patch: Record<string, unknown>) => {
    setError(null);
    try {
      await api.put(`/ma/gwc/people/${person.MaGwcPersonId}`, patch);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save');
    }
  };

  return (
    <Modal
      title={person.Name}
      onClose={onClose}
      footer={
        <>
          <DeleteButton
            what={person.Name}
            onConfirm={() => {
              void api.del(`/ma/gwc/people/${person.MaGwcPersonId}`).then(onDeleted);
            }}
          />
          <button type="button" className="btn btn-primary" onClick={onClose}>
            Done
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}

      <div className="card">
        <div className="card-body tight">
          <span className={`badge ${verdict.badge}`}>{verdict.label}</span>
          <p className="xs muted mt-2">{verdict.advice}</p>
        </div>
      </div>

      <div className="form-grid">
        <Field label="Seat">
          <input
            className="input"
            defaultValue={person.Seat}
            onBlur={(e) => {
              if (e.target.value !== person.Seat) void save({ seat: e.target.value });
            }}
          />
        </Field>
        <Field label="Department">
          <input
            className="input"
            defaultValue={person.Department}
            onBlur={(e) => {
              if (e.target.value !== person.Department) void save({ department: e.target.value });
            }}
          />
        </Field>
      </div>

      {GWC_DIMENSIONS.map((d) => (
        <div className="ma-gwc-dim" key={d.key}>
          <div className="row-between">
            <strong>
              {d.letter} — {d.label}
            </strong>
            <div className="status-group">
              {GWC_ANSWERS.map((a) => (
                <button
                  key={a.value}
                  type="button"
                  className={`status-btn${person[d.key] === a.value ? ' met on' : ''}`}
                  onClick={() => void save({ [d.key.charAt(0).toLowerCase() + d.key.slice(1)]: a.value })}
                >
                  {a.label}
                </button>
              ))}
            </div>
          </div>
          <p className="xs muted mt-2">{d.definition}</p>
          <ul className="xs subtle ma-gwc-prompts">
            {d.prompts.map((q) => (
              <li key={q}>{q}</li>
            ))}
          </ul>
          <textarea
            className="input mt-2"
            rows={2}
            placeholder="Evidence for the call"
            defaultValue={person[d.notesKey]}
            onBlur={(e) => {
              if (e.target.value !== person[d.notesKey])
                void save({ [d.notesKey.charAt(0).toLowerCase() + d.notesKey.slice(1)]: e.target.value });
            }}
          />
        </div>
      ))}

      <Field label="Decision">
        <select
          className="select"
          value={person.Action}
          onChange={(e) => void save({ action: e.target.value })}
        >
          {GWC_ACTIONS.map((a) => (
            <option key={a.value} value={a.value}>
              {a.label}
            </option>
          ))}
        </select>
      </Field>

      <Field label="What was agreed">
        <textarea
          className="input"
          rows={2}
          defaultValue={person.ActionNotes}
          onBlur={(e) => {
            if (e.target.value !== person.ActionNotes) void save({ actionNotes: e.target.value });
          }}
        />
      </Field>
    </Modal>
  );
}

function AddPerson({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState('');
  const [seat, setSeat] = useState('');
  const [department, setDepartment] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.post('/ma/gwc/people', { name: name.trim(), seat, department });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add them');
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Add a person"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={saving || !name.trim()}
            onClick={() => void submit()}
          >
            {saving ? 'Adding…' : 'Add'}
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}
      <Field label="Name">
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
      </Field>
      <div className="form-grid">
        <Field label="Seat">
          <input className="input" value={seat} onChange={(e) => setSeat(e.target.value)} />
        </Field>
        <Field label="Department">
          <input
            className="input"
            value={department}
            onChange={(e) => setDepartment(e.target.value)}
          />
        </Field>
      </div>
    </Modal>
  );
}
